import { Immutable } from "immer";
import React from "react";
import { useStaticURL } from "tsi-common-react/src/utils/urls";

import type { ProductList as Value } from "@reactivated";

import { EditorContent } from "@thelabnyc/thelabui/src/components/EditorContent";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { getLinkUrl, originalUrl } from "../../utils/wagtail";

import leadInStyles from "../../styles/composable/lead-in.module.scss";
import styles from "./index.module.scss";

type IProps = Immutable<{
    value: Value;
}>;

type IItem = NonNullable<Immutable<Value>["products"]>[number];
type IItemChild = NonNullable<IItem["body"]>[number];

/**
 * Verbatim transcription of the legacy
 * ``components/product_lists/product_video.html`` wistia figure: the static
 * popover-embed span/thumb-container markup that the site-wide wistia
 * initializer binds against on load (NO React handlers). The trailing image
 * reproduces the legacy bare ``{% image self.image original %}`` tag output:
 * a plain ``<img>`` with the rendition url/width/height and Wagtail's default
 * alt (the serialized ``default_alt_text``), and nothing at all when no image
 * is set.
 */
const ProductVideoFigure = (props: {
    video: Immutable<Extract<IItemChild, { type: "video" }>["value"]>;
}): React.JSX.Element => {
    const staticURL = useStaticURL();
    const videoId = props.video.wistia_video_id ?? "";
    const image = props.video.image;
    const rendition = image?.renditions["original"];
    return (
        <figure className={styles.prodlistVideo}>
            <span
                className={`wistia_embed wistia_async_${videoId} popover=true popoverContent=link wistia_embed_initialized`}
                id={`wistia-${videoId}-1`}
            >
                <div
                    id="wistia_37.thumb_container"
                    className="wistia_click_to_play"
                    style={{
                        position: "relative",
                        height: "0px",
                        width: "0px",
                    }}
                >
                    {/* TODO: Need link title since content is an image. See #13290 */}
                    <a
                        className={styles.prodlistVideoPlay}
                        href="#"
                        title="Play Video"
                    >
                        <img
                            src={`${staticURL}img/product-list/icon-video-play.png`}
                            alt="Play Icon"
                            loading="lazy"
                            decoding="async"
                        />
                    </a>
                </div>
            </span>
            {image && (
                <img
                    src={originalUrl(image)}
                    alt={image.default_alt_text}
                    width={rendition?.width ?? image.width}
                    height={rendition?.height ?? image.height}
                    loading="lazy"
                    decoding="async"
                />
            )}
        </figure>
    );
};

const renderItemChild = (block: IItemChild): React.JSX.Element => {
    switch (block.type) {
        case "title":
            return (
                <h4 className={styles.prodlistHeadline} key={block.id}>
                    {block.value}
                </h4>
            );
        case "subtitle":
            return (
                <p className={styles.prodlistSubtext} key={block.id}>
                    {block.value}
                </p>
            );
        case "description":
            return (
                <p className={styles.prodlistDescript} key={block.id}>
                    {block.value}
                </p>
            );
        case "price":
            return (
                <p className={styles.prodlistPrice} key={block.id}>
                    {block.value}
                </p>
            );
        case "cta":
            return (
                <a
                    key={block.id}
                    className="button"
                    href={getLinkUrl(block.value)}
                    title={block.value.html_title ?? ""}
                >
                    {block.value.display_name}
                </a>
            );
        case "video":
            return <ProductVideoFigure key={block.id} video={block.value} />;
    }
};

/**
 * Faithful JSX transcription of the legacy sealy
 * ``components/product_lists/product_list.html`` (+ its
 * title/subtitle/description/price/cta/video child templates) (#33285).
 *
 * - ``subtitle`` is a RawHTMLBlock (legacy ``{{ self.subtitle }}`` printed
 *   it unescaped), so it is injected via ``dangerouslySetInnerHTML``.
 * - In the illustration branch the legacy illustration alt expression
 *   (``illustration_image.photo.alt``) resolved to empty, so ``alt=""`` is
 *   preserved verbatim.
 */
export const ProductList = (props: IProps): React.JSX.Element => {
    const value = props.value;
    return (
        <section className="ptb">
            {(value.title || value.subtitle) && (
                <div
                    className={concatClassNames([
                        leadInStyles.leadIn,
                        "container",
                    ])}
                >
                    <h3>{value.title}</h3>
                    <EditorContent>
                        {(marker) => (
                            <p
                                dangerouslySetInnerHTML={{
                                    __html: value.subtitle ?? "",
                                }}
                                {...marker}
                            />
                        )}
                    </EditorContent>
                </div>
            )}
            <div className="container">
                {(value.products ?? []).map((product, index) => (
                    <div className={styles.prodlistItem} key={index}>
                        {product.illustration_image ? (
                            <div
                                className={concatClassNames([
                                    styles.prodlistImage,
                                    styles.prodlistImageIllustration,
                                ])}
                            >
                                <img
                                    className="img-responsive"
                                    src={originalUrl(product.image)}
                                    alt={product.image?.default_alt_text ?? ""}
                                    loading="lazy"
                                    decoding="async"
                                />
                                <img
                                    className={styles.prodlistIllustration}
                                    src={originalUrl(
                                        product.illustration_image,
                                    )}
                                    alt=""
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                        ) : (
                            <picture className={styles.prodlistImage}>
                                <source
                                    srcSet={originalUrl(product.mobile_image)}
                                    media="(max-width: 768px)"
                                />
                                <img
                                    className="img-responsive"
                                    srcSet={originalUrl(product.image)}
                                    alt={product.image?.default_alt_text ?? ""}
                                    loading="lazy"
                                    decoding="async"
                                />
                            </picture>
                        )}
                        <div className={styles.prodlistCopy}>
                            {(product.body ?? []).map(renderItemChild)}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};
